import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  signInWithCustomToken,
  updatePassword,
} from "firebase/auth";
import { api } from "./config/api";
import { auth } from "./config/firebase";
import { AxiosError } from "axios";
import { extractError } from "./config/extractError";
import { captureAppError, shouldCaptureServiceError, type SafeContext } from "../observability/sentry";

function getApiErrorMessage(error: unknown, fallback: string) {
  return error instanceof AxiosError
    ? error.response?.data?.error ?? fallback
    : fallback;
}

function getErrorMessage(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback;
}

function captureAccountServiceError(
  error: unknown,
  action: string,
  extra?: SafeContext,
) {
  if (!shouldCaptureServiceError(error)) return;
  captureAppError(error, {
    area: "account-service",
    action,
    extra,
  });
}

/** Which sign-ins already hold an address, when adding it collides with another account. */
export interface AddEmailConflict {
  code: string;
  providers: string[] | null;
}

/**
 * Adds an email to an account that has none (KAN-622).
 *
 * A different endpoint from `updateHomeowner`, which refuses every email: for an email-first
 * customer the address IS their login credential, so changing one is locked (KAN-635). Filling an
 * empty column is a different operation, and keeping them apart is what lets that lock stay.
 *
 * The address is recorded for DELIVERY immediately and is not a login method until the customer
 * confirms it from their inbox. `code` and `providers` are surfaced so a collision can say
 * "already used with Google" instead of a bare refusal.
 */
export async function addHomeownerEmail(email: string) {
  try {
    const response = await api.patch("/add-email", { email });

    /*
     * ⚠️ SPEND THIS, OR THE CUSTOMER IS SIGNED OUT AT THE NEXT PAGE LOAD (KAN-622).
     *
     * Putting the address on the Firebase user bumps `validSince`, which revokes the refresh
     * token this browser is holding. The ID token in memory keeps working for the rest of its
     * hour, so nothing appears wrong -- the customer finishes the booking form and pays. The
     * session dies on the first FULL PAGE LOAD, which is Stripe redirecting back to
     * `/book/return`: the SDK exchanges a revoked refresh token, gets `TOKEN_EXPIRED`, signs them
     * out, and `ProtectedRoutes` sends them to /login instead of their confirmation page.
     *
     * The server mints a replacement for the same uid (never a stronger one -- see
     * `mintReplacementSession`) and this is where it is spent. It must happen BEFORE the caller
     * reports success, so the session is whole by the time the booking flow moves on.
     */
    const sessionToken = response.data?.sessionToken as string | undefined;
    if (sessionToken) {
      try {
        await signInWithCustomToken(auth, sessionToken);
      } catch (err) {
        /*
         * Not fatal to THIS call: the address is saved, and reporting an error would send the
         * customer back to re-enter an address that is already on their account. Re-submitting
         * the same address is the recovery -- the server hands out a fresh token on that path too.
         */
        captureAccountServiceError(err, "add-homeowner-email-resession");
      }
    }

    return {
      message: (response.data?.message as string) ?? null,
      error: null,
      conflict: null as AddEmailConflict | null,
    };
  } catch (err) {
    captureAccountServiceError(err, "add-homeowner-email");

    const code =
      err instanceof AxiosError
        ? ((err.response?.data?.code as string | undefined) ?? null)
        : null;
    const providers =
      err instanceof AxiosError
        ? ((err.response?.data?.providers as string[] | undefined) ?? null)
        : null;

    return {
      message: null,
      error: getApiErrorMessage(
        err,
        "Couldn't add that email address. Please try again.",
      ),
      // Carried separately from the message so the caller can branch on the case -- a conflict
      // needs support, a validation error needs a retype, and they read differently.
      conflict: code ? { code, providers } : null,
    };
  }
}

export async function updateHomeowner({
  firstName,
  lastName,
  phone,
}: {
  firstName?: string;
  lastName?: string;
  phone?: string;
}) {
  if (!firstName && !lastName && !phone)
    return { message: null, error: "Please fill in the required fields." };
  try {
    const response = await api.patch("/update-details", {
      lastName,
      firstName,
      phone,
    });
    return {
      message: "Successfully updated homeowner!",
      error: null,
      profileUpdatedAt: response.data?.profileUpdatedAt ?? null,
    };
  } catch (err) {
    captureAccountServiceError(err, "update-homeowner", {
      has_first_name: Boolean(firstName),
      has_last_name: Boolean(lastName),
      has_phone: Boolean(phone),
    });

    return {
      message: null,
      error: "Failed to update homeowner. Please try again later.",
      profileUpdatedAt: null,
    };
  }
}

export async function createBillingPortalSession(returnUrl: string) {
  try {
    const response = await api.post("/billing/portal-session", {
      return_url: returnUrl,
    });

    return {
      url: response.data?.url ?? null,
      error: null,
    };
  } catch (err) {
    captureAccountServiceError(err, "create-billing-portal-session");
    return {
      url: null,
      error: getApiErrorMessage(
        err,
        "Failed to open Stripe billing portal. Please try again later.",
      ),
    };
  }
}

//change email for user function---------------------------------------------------------------
import { verifyBeforeUpdateEmail } from "firebase/auth";

export async function updateUserEmail(newEmail: string) {
  try {
    const user = auth.currentUser;

    if (!user) {
      return { message: null, error: "No user logged in." };
    }

    // sends a verification link to the new email.
    await verifyBeforeUpdateEmail(user, newEmail);
    const response = await api.patch("/update-details", { email: newEmail });

    return {
      message:
        "Verification email sent. Please verify your new email address to complete the update.",
      error: null,
      profileUpdatedAt: response.data?.profileUpdatedAt ?? null,
    };
  } catch (error) {
    captureAccountServiceError(error, "update-user-email");
    return {
      message: null,
      error: getErrorMessage(error, "Could not update email."),
      profileUpdatedAt: null,
    };
  }
}

//change password for user function---------------------------------------------------------------
export async function updateUserPassword(
  currentPassword: string,
  newPassword: string,
) {
  try {
    const user = auth.currentUser;
    if (!user || !user.email) {
      return { message: null, error: "No logged-in user with email found." };
    }

    //reauthenticate with current password
    const credential = EmailAuthProvider.credential(
      user.email,
      currentPassword,
    );
    await reauthenticateWithCredential(user, credential);

    //update password
    await updatePassword(user, newPassword);
    return { message: "Password successfully updated", error: null };
  } catch (err) {
    //return specific error messages
    const code =
      err instanceof Error && "code" in err ? String(err.code ?? "") : "";
    if (code === "auth/invalid-credential") {
      return {
        message: null,
        error:
          "Incorrect password entered. Please check your credentials and try again.",
      };
    }
    captureAccountServiceError(err, "update-user-password");
    return {
      message: null,
      error: getErrorMessage(err, "Failed to change password."),
    };
  }
}

export async function setUserPassword(newPassword: string) {
  try {
    const user = auth.currentUser;
    if (!user || !user.email) {
      return {
        message: null,
        error: "Add and confirm an email address before setting a password.",
      };
    }

    await updatePassword(user, newPassword);
    await user.reload();
    return { message: "Password set successfully.", error: null };
  } catch (err) {
    const code =
      err instanceof Error && "code" in err ? String(err.code ?? "") : "";
    if (code === "auth/requires-recent-login") {
      return {
        message: null,
        error:
          "Please sign out and sign in with your phone number again before setting a password.",
      };
    }
    if (code === "auth/weak-password") {
      return {
        message: null,
        error: "Please choose a stronger password.",
      };
    }
    captureAccountServiceError(err, "set-user-password");
    return {
      message: null,
      error: getErrorMessage(err, "Failed to set password."),
    };
  }
}

/**
 * "Did the code to this number bounce off WhatsApp?" for the shared code flows (account settings,
 * booking change-number). Twilio accepts a send to a number with no WhatsApp; the rejection only
 * reaches the backend seconds later. Polled by the OTP dialog via `useNotOnWhatsappWatch`.
 *
 * Never throws, and true only when the backend said so.
 */
export async function checkOtpStatus(contactNo: string): Promise<boolean> {
  try {
    const res = await api.post("/otp-status", { contactNo });
    return res.data?.notOnWhatsapp === true;
  } catch {
    return false;
  }
}

//For sending OTP's from twilio (NOT firebase!! Homeowner phone numbers are stored in azure, not firebase)
export async function sendTwilioOTPService(contactNo: string) {
  try {
    await api.post("/send-otp", { contactNo });
    return { message: "OTP successfully sent!", error: null, code: null as string | null };
  } catch (err) {
    // Timeout: Twilio likely received the request before the connection dropped —
    // the OTP may still arrive, so callers should open the entry modal.
    if (err instanceof AxiosError && err.code === "ECONNABORTED") {
      return {
        message: null,
        error:
          "This is taking longer than usual — check your WhatsApp, or wait 30 seconds before retrying.",
        code: "TIMEOUT" as const,
      };
    }
    // 4xx: pass the backend's user-facing message through; skip Sentry.
    const status = err instanceof AxiosError ? (err.response?.status ?? 0) : 0;
    if (status >= 400 && status < 500) {
      const backendMessage =
        (err as AxiosError<{ error?: string }>).response?.data?.error ??
        "Failed to send OTP. Please try again later.";
      // Distinguish per-phone cooldown (30 s) from harder rate limits so
      // the caller can show a countdown rather than a generic error.
      const code = backendMessage.includes("Please wait")
        ? ("OTP_SEND_COOLDOWN" as const)
        : null;
      return { message: null, error: backendMessage, code };
    }
    captureAccountServiceError(err, "send-twilio-otp");
    return {
      message: null,
      error: "Failed to send OTP. Please try again later.",
      code: null as string | null,
    };
  }
}

export interface PaymentTransaction {
  session_date: string;
  payment_date: string;
  amount: number;
  original_amount: number | null;
  promo_discount_amount: number | null;
  promo_code: string | null;
  promo_bonus_aircon_units: number | null;
  deferred_promo_code: string | null;
  deferred_promo_discount_value: number | null;
  deferred_promo_max_per_user: number | null;
  service_name: string;
  frequency: string;
  booking_id: number;
  payment_status: string;
  is_early_termination: boolean;
}

export interface ActiveSubscription {
  booking_id: number;
  service_name: string;
  frequency: string;
  start_date: string;
  start_time: string;
  end_time: string;
  location: string;
  charge_amount: number | null;
  next_session_date: string | null;
  next_charge_date: string | null;
  // Referral hours already redeemed against this plan's next invoice. charge_amount is
  // the preview's amount_due, so it is already net of this credit.
  referral_credit_hours?: number;
  referral_credit_cents?: number;
}

export interface SubscriptionReferralPreview {
  booking_id: number;
  duration_hours: number;
  /** Ceiling on redeemable hours — one billing cycle's worth. */
  max_hours: number;
  hours_applied: number;
  discount_cents: number;
  per_hour_rate_cents: number;
  /** Credit already applied to this plan's next invoice; blocks a further redemption. */
  pending_credit_hours?: number;
  pending_credit_cents?: number;
  /**
   * False when Stripe has stopped billing this plan (canceled / unpaid / expired). There is
   * no future invoice for a credit to come off, so redemption must not be offered.
   */
  subscription_billable?: boolean;
  /**
   * True when this plan has a failed payment Stripe is still retrying (`past_due`).
   * Redemption is still allowed, but the hours are lost if collection never succeeds.
   */
  payment_at_risk?: boolean;
}

/**
 * Referral redemption against a running subscription. The discount arrives as a Stripe
 * account credit drawn down by the next invoice, not as a change to the recurring price.
 */
export async function fetchSubscriptionReferralPreview(bookingId: number, hours?: number) {
  try {
    const response = await api.get(`/subscriptions/${bookingId}/referral-preview`, {
      params: hours !== undefined ? { hours } : undefined,
    });
    return { data: response.data as SubscriptionReferralPreview, error: null };
  } catch (err) {
    captureAccountServiceError(err, "subscription-referral-preview");
    return { data: null, error: extractError(err) };
  }
}

export async function applySubscriptionReferral(bookingId: number, hours: number) {
  try {
    const response = await api.post(`/subscriptions/${bookingId}/apply-referral`, { hours });
    return {
      data: response.data as {
        booking_id: number;
        hours_applied: number;
        discount_cents: number;
        /** Everything now sitting against the next invoice, including earlier redemptions. */
        total_credit_hours?: number;
        total_credit_cents?: number;
      },
      error: null,
    };
  } catch (err) {
    captureAccountServiceError(err, "subscription-referral-apply");
    return { data: null, error: extractError(err) };
  }
}

export async function fetchPaymentHistory() {
  try {
    const response = await api.get("/payment-history");
    return { payments: response.data.payments as PaymentTransaction[], error: null };
  } catch (err) {
    captureAccountServiceError(err, "fetch-payment-history");
    return { payments: [] as PaymentTransaction[], error: "Failed to load payment history." };
  }
}

export async function checkHasSavedCard() {
  try {
    const response = await api.get("/has-saved-card");
    return { hasSavedCard: response.data.hasSavedCard as boolean, error: null };
  } catch (err) {
    captureAccountServiceError(err, "check-has-saved-card");
    return { hasSavedCard: false, error: "Failed to check saved card." };
  }
}

export async function fetchActiveSubscriptions() {
  try {
    const response = await api.get("/active-subscriptions");
    return { subscriptions: response.data.subscriptions as ActiveSubscription[], error: null };
  } catch (err) {
    captureAccountServiceError(err, "fetch-active-subscriptions");
    return { subscriptions: [] as ActiveSubscription[], error: "Failed to load active subscriptions." };
  }
}

export async function verifyTwilioOTPService(contactNo: string, otp: string) {
  try {
    const twilioResponse = await api.post("/verify-otp", { contactNo, otp });
    if (twilioResponse.data.status == "approved") {
      return { message: "OTP successfully verified!", error: null };
    } else {
      return {
        message: null,
        error: "Invalid or expired OTP. Please try again.",
      };
    }
  } catch (err) {
    captureAccountServiceError(err, "verify-twilio-otp");
    return { message: null, error: "Failed to verify OTP" };
  }
}

/**
 * Attaches an OTP-verified number to the account the user is already signed in to (KAN-666).
 *
 * Call ONLY after `verifyTwilioOTPService` has returned success. The backend spends the
 * server-side proof that verification minted, so calling without one is refused -- the OTP is not
 * re-sent or re-checked here.
 *
 * Backend messages are passed straight through for 4xx. Every refusal this endpoint produces is
 * something the customer has to act on -- the number belongs to another account, or to a team
 * member, or their account already has a different one -- and a generic message would leave them
 * with no idea which.
 */
export async function linkPhoneSignIn(contactNo: string) {
  try {
    const response = await api.post("/link-phone-signin", { contactNo });
    return {
      linked: true,
      alreadyLinked: Boolean(response.data?.alreadyLinked),
      error: null as string | null,
      code: null as string | null,
    };
  } catch (err) {
    const status = err instanceof AxiosError ? (err.response?.status ?? 0) : 0;
    if (status >= 400 && status < 500) {
      const data = err instanceof AxiosError ? err.response?.data : null;
      return {
        linked: false,
        alreadyLinked: false,
        error:
          (data?.error as string | undefined) ??
          "We couldn't add phone sign-in. Please try again.",
        code: (data?.code as string | undefined) ?? null,
      };
    }
    captureAccountServiceError(err, "link-phone-signin");
    return {
      linked: false,
      alreadyLinked: false,
      error: "We couldn't add phone sign-in. Please try again.",
      code: null as string | null,
    };
  }
}

export interface IssuedVoucher {
  id: number;
  promo_source: "issued" | "public";
  code: string;
  title: string;
  discount_type: "fixed" | "fixed_hourly_rate" | "fixed_unit_rate" | "percent" | "free_hours";
  discount_value: number;
  max_redemptions_per_user: number | null;
  bonus_aircon_units: number | null;
  promotion_credit_id: number;
  applies_to: string;
  source: string;
  issued_at: string;
  expires_at: string | null;
  is_expiring_soon: boolean;
  days_left: number | null;
}

export interface LockedVoucher {
  id: number;
  booking_id: number;
  code: string;
  title: string;
  discount_type: "fixed" | "fixed_hourly_rate" | "fixed_unit_rate" | "percent" | "free_hours";
  discount_value: number;
  max_redemptions_per_user: number | null;
  bonus_aircon_units: number | null;
  frequency: string;
  service_id: number;
  service_name: string;
  next_session_date: string | null;
  created_at: string;
  // The grant's own lifetime, independent of the campaign's expires_at. A quarterly
  // aircon grant is redeemed months after the campaign ends, so these are the only
  // dates that mean anything to the customer.
  remaining_uses: number;
  expires_at: string | null;
  is_expiring_soon: boolean;
  days_left: number | null;
}

export interface UsedVoucher {
  id: number;
  code: string;
  title: string;
  discount_type: "fixed" | "fixed_hourly_rate" | "fixed_unit_rate" | "percent" | "free_hours";
  discount_value: number;
  bonus_aircon_units: number | null;
  booking_id: number;
  session_date: string | null;
  service_id: number;
  service_name: string;
  amount_saved: number | null;
  used_at: string;
}

export interface ReferralEntry {
  id: number;
  status: "pending" | "qualified" | "rewarded" | "expired";
  qualified_at: string | null;
  rewarded_at: string | null;
  created_at: string;
  referee_full_name: string | null;
}

export interface ReferralSummary {
  referralCode: string | null;
  referrals: ReferralEntry[];
}

export interface RewardsData {
  lockedVouchers: LockedVoucher[];
  issuedVouchers: IssuedVoucher[];
  usedVouchers: UsedVoucher[];
  summary: {
    total_saved: number;
    active_count: number;
  };
  referral: ReferralSummary;
}

export async function fetchRewards() {
  try {
    const response = await api.get("/rewards");
    return { data: response.data as RewardsData, error: null };
  } catch (err) {
    captureAccountServiceError(err, "fetch-rewards");
    return { data: null, error: "Failed to load rewards. Please try again." };
  }
}

export async function resolveReferralCode(code: string) {
  try {
    const response = await api.get(`/referral/${encodeURIComponent(code)}`);
    return {
      data: response.data as { valid: boolean; referrerFullName?: string },
      error: null,
    };
  } catch {
    return { data: { valid: false }, error: null };
  }
}

// Account deletion (KAN-598) -------------------------------------------------------------
//
// Deletion is a SOFT delete: the account is marked and access blocked, but bookings and
// payments are retained for the 5-year GST record-keeping window.

export type DeletionBlockerCode =
  | "ACTIVE_SUBSCRIPTION"
  | "UPCOMING_SESSIONS"
  | "UNUSED_PACKAGE_CREDITS";

export type DeletionWarningCode =
  | "REFERRAL_VALUE_FORFEITED"
  | "PENDING_BOOKINGS_EXPIRED";

export interface DeletionBlocker {
  code: DeletionBlockerCode;
  count?: number;
  bookingIds?: number[];
  nextSessionDate?: string;
  creditsRemaining?: number;
}

export interface DeletionWarning {
  code: DeletionWarningCode;
  count?: number;
  voucherCount?: number;
  pendingReferralCount?: number;
}

export interface DeletionEligibility {
  canDelete: boolean;
  alreadyRequested: boolean;
  blockers: DeletionBlocker[];
  warnings: DeletionWarning[];
}

/**
 * What is standing between this customer and deletion.
 *
 * The server returns codes rather than sentences so the copy lives here, next to the links
 * that resolve each blocker.
 */
export async function getAccountDeletionEligibility() {
  try {
    const response = await api.get("/account/deletion-eligibility");
    return { data: response.data as DeletionEligibility, error: null };
  } catch (err) {
    captureAccountServiceError(err, "account-deletion-eligibility");
    return {
      data: null,
      error: getApiErrorMessage(
        err,
        "Couldn't check your account status. Please try again.",
      ),
    };
  }
}

/**
 * Requests deletion. `confirmationText` must be the word the dialog asked them to type —
 * the server checks it too, since a text field is not a security boundary.
 *
 * On a 409 the server returns the blocker list, which is passed back so the dialog can
 * refresh in place rather than replacing everything with a bare error.
 */
export async function deleteAccount(confirmationText: string) {
  try {
    await api.delete("/account", { data: { confirmationText } });
    return { success: true, error: null, blockers: null };
  } catch (err) {
    captureAccountServiceError(err, "account-deletion-request");
    const blockers =
      err instanceof AxiosError
        ? ((err.response?.data?.blockers as DeletionBlocker[] | undefined) ?? null)
        : null;
    return {
      success: false,
      error: getApiErrorMessage(err, "Couldn't delete your account. Please try again."),
      blockers,
    };
  }
}
